import React from "react";

const normalizeHref = (raw) => {
  const v = (raw || "").trim();
  if (!v) return "";

  const lower = v.toLowerCase();

  if (lower.startsWith("tel:") || lower.startsWith("mailto:")) return v;

  const phoneLike = /^\+?\d[\d\s-]{6,}$/.test(v);
  if (phoneLike) {
    const digits = v.replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  if (lower.startsWith("wa:") || lower.startsWith("whatsapp:")) {
    const digits = v.split(":").slice(1).join(":").replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  if (/^https?:\/\//i.test(v)) return v;
  if (lower.startsWith("www.")) return `https://${v}`;
  if (v.startsWith("/")) return v;

  return `https://${v}`;
};

const goToLink = (raw) => {
  const href = normalizeHref(raw);
  if (!href) return;

  const isExternal =
    /^https?:\/\//i.test(href) || /^mailto:/i.test(href) || /^tel:/i.test(href);

  if (isExternal) window.open(href, "_blank", "noopener,noreferrer");
  else window.location.href = href;
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const pickAlign = (style, config, fallback) => {
  const v = String(
    style?.textAlign || style?.alignment || config?.alignment || ""
  )
    .toLowerCase()
    .trim();
  if (v === "left" || v === "center" || v === "right") return v;
  return fallback;
};

const panelStyle = (bg, border) => ({
  backgroundColor: bg || undefined,
  borderColor: border || undefined,
});

const textColor = (value) => ({ color: value || undefined });

const LocationSection1 = ({ config, style }) => {
  const bg = buildBg(style, "bg-gray-50");

  const fallbackAlign = (config?.direction || "ltr") === "rtl" ? "right" : "left";
  const align = pickAlign(style, config, fallbackAlign);
  const alignMap = { left: "text-left", center: "text-center", right: "text-right" };
  const alignClass = alignMap[align] || alignMap.left;

  const address = config.address || "123 Business Street, City, State 12345";
  const city = config.city || "New York";
  const state = config.state || "NY";
  const zip = config.zip || "10001";
  const country = config.country || "United States";
  const phone = config.phone || "+1 (555) 123-4567";
  const email = config.email || "hello@business.com";
  const hours = config.hours || "Mon-Fri: 9am - 6pm, Sat: 10am - 4pm, Sun: Closed";

  const mapEmbedUrl =
    config.mapEmbedUrl ||
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.914770355!2d-74.119763!3d40.697403!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY!5e0!3m2!1sen!2sus!4v1644262071400!5m2!1sen!2sus";

  const directionLink = config.directionLink || "";
  const directionDisabled = !!directionLink && !normalizeHref(directionLink);

  return (
    <section
      dir={config?.direction || "ltr"}
      style={bg.style}
      className={`py-20 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-7xl">
        <div className={`mb-12 ${alignClass}`}>
          <h2
            style={{ color: style?.titleColor || undefined }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
          >
            {config.title || "Visit Our Location"}
          </h2>
          <p
            style={{ color: style?.subtitleColor || undefined }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {config.subtitle || "We'd love to meet you in person"}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div
            className="rounded-2xl overflow-hidden shadow-xl h-[400px] lg:h-[500px] border"
            style={panelStyle(style?.locationCardBg, style?.locationCardBorder)}
          >
            <iframe
              title="Business Location"
              src={mapEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full"
            />
          </div>

          <div
            className={`rounded-2xl shadow-xl p-8 border ${alignClass}`}
            style={panelStyle(style?.locationCardBg || "#ffffff", style?.locationCardBorder)}
          >
            <h3
              className="text-2xl font-bold mb-6"
              style={textColor(style?.locationNameColor)}
            >
              {config.businessName || "Our Office"}
            </h3>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div
                  className="flex-shrink-0"
                  style={textColor(style?.locationIconColor || "#2563eb")}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold mb-1" style={textColor(style?.locationTextColor)}>
                    Address
                  </p>
                  <p style={textColor(style?.locationMutedTextColor)}>{address}</p>
                  <p style={textColor(style?.locationMutedTextColor)}>{`${city}, ${state} ${zip}`}</p>
                  <p style={textColor(style?.locationMutedTextColor)}>{country}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div
                  className="flex-shrink-0"
                  style={textColor(style?.locationIconColor || "#2563eb")}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold mb-1" style={textColor(style?.locationTextColor)}>
                    Contact
                  </p>
                  <p style={textColor(style?.locationMutedTextColor)}>Phone: {phone}</p>
                  <p
                    className="break-words"
                    style={textColor(style?.locationMutedTextColor)}
                  >
                    Email: {email}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div
                  className="flex-shrink-0"
                  style={textColor(style?.locationIconColor || "#2563eb")}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold mb-1" style={textColor(style?.locationTextColor)}>
                    Business Hours
                  </p>
                  <p style={textColor(style?.locationMutedTextColor)}>{hours}</p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              {directionLink ? (
                <button
                  disabled={directionDisabled}
                  onClick={() => !directionDisabled && goToLink(directionLink)}
                  style={{
                    backgroundColor: style?.primaryBtnBg || undefined,
                    color: style?.primaryBtnText || undefined,
                  }}
                  className={`flex-1 font-semibold py-3 px-6 rounded-lg transition duration-300 ${
                    directionDisabled
                      ? "opacity-40 cursor-not-allowed"
                      : style?.primaryBtnBg
                      ? "hover:opacity-90"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  Get Directions
                </button>
              ) : null}

              {phone ? (
                <button
                  onClick={() => goToLink(`tel:${phone}`)}
                  style={{
                    backgroundColor: style?.ctaSecondaryBtnBg || undefined,
                    color: style?.secondaryBtnText || undefined,
                    borderColor: style?.secondaryBtnBorder || undefined,
                  }}
                  className={`flex-1 font-semibold py-3 px-6 rounded-lg border-2 transition duration-300 ${
                    style?.secondaryBtnBorder || style?.secondaryBtnText || style?.ctaSecondaryBtnBg
                      ? "hover:opacity-90"
                      : "border-gray-300 hover:border-blue-600 text-gray-700 hover:text-blue-600"
                  }`}
                >
                  Call Us
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationSection1;